import {
  useGetCustomExerciseTypes,
  useGetExerciseTypesSearch
} from '#api/exercise-types';
import { PurplePlus } from '#assets/svg';
import CheckBoxAtom from '#atoms/Checkbox';
import { useDashboardChartFormProcess } from '#pages/DashboardChartForm/hooks/useDashboardChartFormProcess';
import { useDashboardCharFormExerciseState } from '#pages/DashboardChartForm/states/dashboardCharFormExerciseState';
import { ButtonSubmit } from '#pages/WeeklyCalenderScheduleRegistration/styles';
import { useSelectedExerciseListRecoil } from '#states/exerciseState';
import { PrevHeaderBarWithTitleTemplate } from '#templates';
import { ICustomExerciseType } from '#types/exerciseTypes';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import ChipButtons from 'src/components/shared/ChipButtons';
import SearchInput from 'src/shared/components/input/SearchInput';
import { AddCustomExercise } from './AddCustomExercise';
import { SelectedExerciseList } from './SelectedExerciseList';
import { StyledListItem } from './styles';

export const SelectExercisePage = () => {
  // 운동 부위 예) 가슴, 등, 하체
  const [exercisePart, setExercisePart] = useState<string>('ALL');

  // 운동 검색 키워드
  const [exerciseKeyword, setExerciseKeyword] = useState<string>('');

  const [openAddCustomExerciseModal, setOpenAddCustomExerciseModal] =
    useState<boolean>(false);

  // 운동 가이드 검색 리스트
  const { data: exerciseList } = useGetExerciseTypesSearch({
    name: exerciseKeyword
  });

  // 코치의 커스텀 운동 리스트
  const { data: customExercises } = useGetCustomExerciseTypes();

  // 선택되지 않은 운동 리스트
  const [unSelectedExerciseList, setUnSelectedExerciseList] = useState<
    ICustomExerciseType[]
  >([]);

  // 선택된 운동 리스트
  const { selectedExerciseList, setSelectedExerciseList } =
    useSelectedExerciseListRecoil();

  // 운동 차트 작성에 들어갈 운동 리스트
  const { setRecords } = useDashboardCharFormExerciseState();

  // 커스텀만 보기
  const [checked, setChecked] = useState<boolean>(false);

  // 검색결과 없음
  const noSearchResult = exerciseKeyword && exerciseList?.length === 0;

  // 커스텀 운동 없음
  const noCustomExercise = checked && customExercises?.data.length === 0;

  // 선택된 운동들을 제외한 커스텀 운동 리스트
  const filteredCustomExercises = customExercises?.data?.filter(
    customExercise =>
      !selectedExerciseList.find(
        selectedExercise => selectedExercise.name === customExercise.name
      )
  );

  // 화면에 렌더링할 운동 리스트
  const renderedExerciseList = checked
    ? filteredCustomExercises
    : unSelectedExerciseList;

  const EXERCISE_PART_LIST = [
    { text: '전체', value: 'ALL' },
    { text: '가슴', value: 'CHEST' },
    { text: '등', value: 'BACK' },
    { text: '어깨', value: 'SHOULDER' },
    { text: '하체', value: 'LEG' },
    { text: '이두', value: 'BICEPS' },
    { text: '삼두', value: 'TRICEPS' },
    { text: '유산소', value: 'CARDIO' },
    { text: '코어', value: 'CORE' }
  ];

  useEffect(() => {
    setUnSelectedExerciseList(exerciseList || []);
  }, [exerciseList]);

  useEffect(() => {
    setUnSelectedExerciseList(
      exerciseList?.filter(exercise => {
        if (exercisePart === 'ALL') return true;

        return exercise.part === exercisePart;
      }) || []
    );
  }, [exercisePart, exerciseKeyword]);

  const { setTab } = useDashboardChartFormProcess();

  const onSubmit = () => {
    setRecords(
      selectedExerciseList.map(exercise => ({
        exerciseKind: exercise.name,
        exerciseTypeId: exercise.id,
        sets: [{ setType: 'REPWEIGHT', round: 1 }]
      }))
    );

    setTab('폼');
  };

  const { pathname } = useLocation();

  useEffect(() => {
    setSelectedExerciseList([]);
  }, [pathname]);

  return (
    <PrevHeaderBarWithTitleTemplate
      title="운동 선택"
      rightContents={
        <ButtonSubmit
          disabled={selectedExerciseList.length === 0}
          onClick={onSubmit}
        >
          완료
        </ButtonSubmit>
      }
      onPrevCallback={() => setSelectedExerciseList([])}
      defaultBackground
    >
      <div className="p-20">
        <div className="flex flex-col gap-12">
          <SearchInput
            placeholder={'운동을 검색해주세요'}
            onChange={e => {
              setExerciseKeyword(e.target.value);
            }}
          />
          <div className="w-[calc(100vw-40px)] overflow-x-auto">
            <ChipButtons
              selectedValue={exercisePart || 'ALL'}
              options={EXERCISE_PART_LIST}
              onChange={value => setExercisePart(value)}
            />
          </div>
        </div>
        <div className="flex items-center justify-between pt-20">
          <div className="text-[#938f9e] text-14 font-['Pretendard Variable'] leading-tight">
            총 {exerciseList?.length}개
          </div>

          <CheckBoxAtom
            id={'only custom'}
            label="커스텀만 보기"
            labelStyle={{
              color: 'var(--Gray-Scale-500, #948F9E)',
              textAlign: 'center',
              fontFamily: 'Pretendard Variable',
              fontSize: '16px',
              fontStyle: 'normal',
              fontWeight: 500,
              lineHeight: '21px', // 131.25%
              letterSpacing: '-0.32px'
            }}
            checked={checked}
            onClick={() => setChecked(!checked)}
          />
        </div>

        <div className="relative h-[calc(100vh-242px)] overflow-y-auto mt-12 overflow-hidden">
          <SelectedExerciseList
            onClick={exercise => {
              // 선택된 항목은 selectedExerciseList에서 제거
              setSelectedExerciseList(item =>
                item.filter(item => item.name !== exercise.name)
              );

              // 선택된 항목은 renderExerciseList에 추가
              setUnSelectedExerciseList(prev => [...prev, exercise]);
            }}
            selectedExerciseList={selectedExerciseList}
            setSelectedExerciseList={setSelectedExerciseList}
          />

          {noSearchResult && (
            <div className="px-20 py-40 text-center text-[#938f9e] text-[17px] font-medium font-['Pretendard Variable'] leading-snug">
              검색 결과가 없습니다.
            </div>
          )}

          {!exerciseKeyword && noCustomExercise && (
            <div className="px-20 py-40 text-center text-[#938f9e] text-[17px] font-medium font-['Pretendard Variable'] leading-snug">
              커스텀 운동이 없습니다.
            </div>
          )}

          <div className="flex flex-col gap-12">
            {renderedExerciseList?.map((exercise, index) => (
              <StyledListItem
                key={index}
                onClick={() => {
                  // 선택된 항목은 selectedExerciseList에 추가
                  setSelectedExerciseList(prev => [...prev, exercise]);

                  // 선택된 항목은 unSelectedExerciseList에서 제거
                  setUnSelectedExerciseList(
                    prev => prev?.filter(item => item.name !== exercise.name)
                  );
                }}
              >
                {exercise.name}
              </StyledListItem>
            ))}
          </div>

          {checked && (
            <div
              onClick={() => setOpenAddCustomExerciseModal(true)}
              className="mt-20 h-[54px] px-10 py-4 rounded-lg border-1 border-solid border-[#9170f2] flex justify-center items-center gap-4 text-[#7750e9] text-[17px] font-medium font-[Pretendard Variable] leading-snug"
            >
              <PurplePlus />
              <div>커스텀 운동 추가하기</div>
            </div>
          )}

          {openAddCustomExerciseModal && (
            <AddCustomExercise
              openAddCustomExerciseModal={openAddCustomExerciseModal}
              setOpenAddCustomExerciseModal={setOpenAddCustomExerciseModal}
            />
          )}
        </div>
      </div>
    </PrevHeaderBarWithTitleTemplate>
  );
};
